import { useForm, useStore } from '@tanstack/react-form'
import toNumber from 'lodash/toNumber'
import React, { useContext, useState } from 'react'

import SvgAngleRight from '@Assets/SVG/AngleRight'
import { BuyButtonV2 } from '@Components/BuyButton/v2'
import { Drawer } from '@Components/Drawer'
import { DrawerContentBasic } from '@Components/Drawer/Content'
import { Link } from '@Components/Link'
import { ProductContext } from '@Components/Product/Context'
import { Button } from '@CoreComponents/Buttons/Button'
import { ProductShipping } from '@Domains/Product/Features/Display/ProductShipping'
import { ProductUSPsLegacy } from '@Domains/Product/Features/Display/ProductUSPsLegacy'
import { TitleAndPrice } from '@Domains/Product/Features/Display/TitleAndPrice/TitleAndPrice'
import { GiftCardCustomAmount } from '@Domains/Product/Features/GiftCard/CustomAmount'
import { GiftcardAmountOptions, useGiftCardAmountOptions } from '@Domains/Product/Features/GiftCard/Hooks'
import { GiftCardMessageInput } from '@Domains/Product/Features/GiftCard/MessageInput'
import { GiftCardAmountOptionList } from '@Domains/Product/Features/GiftCard/Options'
import { useTranslation } from '@Globals/Translations'

import styles from './GiftCard.module.scss'

export const GiftCardInfo = () => {
  const { t } = useTranslation()
  const product = useContext(ProductContext)
  const [amountDrawerOpen, setAmountDrawerOpen] = useState(false)
  const isInStock = product?.availability?.isInStock
  // virtual products are never shipped, so there is no shipping countdown
  const isVirtual = product?.masterVariant?.isVirtual
  const { amountOptions, maxValue, minValue } = useGiftCardAmountOptions()

  // The form is the single source of truth for the gift card selection: the drawer
  // body and footer render inside the <form>, while the trigger label and the buy
  // button below read the same values live via useStore.
  const form = useForm({
    defaultValues: {
      amount: GiftcardAmountOptions.SELECT_AMOUNT as string,
      customAmount: '',
      message: '',
    },
    onSubmit: () => setAmountDrawerOpen(false),
  })

  const amount = useStore(form.store, state => state.values.amount)
  const customAmount = useStore(form.store, state => state.values.customAmount)
  const message = useStore(form.store, state => state.values.message)

  if (!product) {
    return <>404</>
  }

  const isInRange = (value: string) => +value >= minValue && +value <= maxValue

  const amountNumber = amount === GiftcardAmountOptions.CUSTOM_AMOUNT ? +customAmount : toNumber(amount)
  const amountIsChosen = amountNumber >= minValue && amountNumber <= maxValue
  const amountLabel = amount === GiftcardAmountOptions.CUSTOM_AMOUNT
    ? customAmount
    : amountOptions.find(option => option.key === amount)?.value

  const customAmountValidator = ({ value }: { value: string }) => {
    if (isInRange(value)) {
      return undefined
    }

    return t('giftcard:GIFT_CARD_RANGE_ERROR', 'The amount must be between {{arg1}} and {{arg2}}', { arg1: minValue, arg2: maxValue })
  }

  return (
    <>
      {product.brand && (
        <Link className={styles.brand} href={product.brand.url?.toString()}>
          <img
            alt={product.brand.id || ''}
            src={product.brand.logo || ''}
            title={product.brand.title || ''}
          />
        </Link>
      )}

      <div>
        <TitleAndPrice
          id={product.id}
          name={product.name}
        />

        {isInStock && (
          <div className={styles.addToCartWrapper}>
            <form.Field name="message">
              {field => (
                <GiftCardMessageInput
                  message={field.state.value}
                  onchange={value => field.handleChange(value)}
                  placeholder={t('giftcard:GIFT_CARD_MESSAGE_PLACEHOLDER', 'Your message goes here')}
                />
              )}
            </form.Field>

            <div className={styles.buyButtonWrapper}>
              <Drawer
                alignment="right"
                onOpenChange={open => setAmountDrawerOpen(open)}
                open={amountDrawerOpen}
                triggerRender={props => (
                  <button
                    {...props}
                    className={`${styles.selectWrapper} ${amountIsChosen ? '' : styles.other}`}
                    type="button"
                  >
                    <p className={styles.textSelectButton}>
                      {amountIsChosen ? amountLabel : t('giftcard:GIFT_CARD_SELECT_AMOUNT')}
                    </p>
                    <SvgAngleRight className={styles.angleRight} />
                  </button>
                )}
              >
                <form
                  className={styles.amountForm}
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault()
                    form.handleSubmit().catch(() => {
                      // Validation failures surface inline; swallow to avoid an unhandled rejection.
                    })
                  }}
                >
                  <DrawerContentBasic
                    footer={(
                      <Button className={styles.buyButton} theme="black" type="submit">
                        {t('cms:COF_SELECT')}
                      </Button>
                    )}
                    title={t('giftcard:GIFT_CARD_SELECT_AMOUNT')}
                  >
                    <form.Field name="amount">
                      {amountField => (
                        <GiftCardAmountOptionList field={amountField} options={amountOptions}>
                          {amountField.state.value === GiftcardAmountOptions.CUSTOM_AMOUNT && (
                            <form.Field name="customAmount" validators={{ onChange: customAmountValidator }}>
                              {field => <GiftCardCustomAmount field={field} />}
                            </form.Field>
                          )}
                        </GiftCardAmountOptionList>
                      )}
                    </form.Field>
                  </DrawerContentBasic>
                </form>
              </Drawer>

              <BuyButtonV2
                className={styles.buyButton}
                color="black"
                disabled={amount === GiftcardAmountOptions.CUSTOM_AMOUNT && !isInRange(customAmount)}
                productID={product.id}
                products={[
                  ({
                    giftcard: {
                      amount: amountIsChosen ? amountNumber : 0,
                      // An empty text must go out as undefined: the server only prompts
                      // "add a message?" when text is not a string, and '' would skip it.
                      text: message || undefined,
                    },
                    productID: product.id,
                    quantity: 1,
                  }),
                ]}
                quantity={1}
              >
                {t('cms:ADD_TO_CART')}
              </BuyButtonV2>
            </div>
          </div>
        )}
      </div>

      {isInStock && !isVirtual && <ProductShipping />}

      <ProductUSPsLegacy />
    </>
  )
}
